/*
 * Personnalisations Le Dressing d'Axelle.
 */

/* ------------------------------------------------------------------
 * 1. En-tete opaque au defilement.
 *
 * Le theme rend l'en-tete opaque (sortie du mode transparent de la page
 * d'accueil) en surveillant le moment ou il sort de l'ecran. Depuis que la
 * bande d'annonces et l'en-tete sont colles ensemble en haut (voir custom.css),
 * l'en-tete ne sort plus jamais de l'ecran : ce detecteur ne se declenche donc
 * plus. On reproduit ici le meme comportement, base sur le defilement.
 * ------------------------------------------------------------------ */
(() => {
  const header = document.querySelector('#header-component[sticky="always"]');
  if (!header) return;

  const majEtat = () => {
    const defile = (document.scrollingElement?.scrollTop ?? window.scrollY) > 0;
    header.dataset.stickyState = defile ? 'active' : 'inactive';
  };

  majEtat();
  window.addEventListener('scroll', majEtat, { passive: true });
})();

/* ------------------------------------------------------------------
 * 2. Sens de defilement de la bande d'annonces.
 *
 * Le theme se contente de basculer aria-hidden sur chaque message. En CSS seul,
 * le message qui s'en va et ceux qui attendent leur tour sont indistinguables :
 * le message entrant repartait donc du meme cote que le sortant.
 * On pose ici une classe explicite au moment du changement :
 *   .annonce-sort  -> sortie vers la gauche
 *   .annonce-entre -> entree depuis la droite
 * ------------------------------------------------------------------ */
(() => {
  const barre = document.querySelector('.announcement-bar');
  if (!barre) return;

  const messages = barre.querySelectorAll('.announcement-bar__slide');
  if (messages.length < 2) return;

  const observateur = new MutationObserver((changements) => {
    for (const changement of changements) {
      const message = changement.target;
      const masque = message.getAttribute('aria-hidden') === 'true';

      // Le theme reecrit aria-hidden sur tous les messages a chaque tour,
      // meme ceux qui ne changent pas. On ignore ces reecritures a l'identique,
      // sinon les messages en attente rejoueraient leur animation de sortie.
      if (changement.oldValue === message.getAttribute('aria-hidden')) continue;

      message.classList.remove('annonce-sort', 'annonce-entre');
      void message.offsetWidth; // force le navigateur a rejouer l'animation
      message.classList.add(masque ? 'annonce-sort' : 'annonce-entre');
    }
  });

  for (const message of messages) {
    observateur.observe(message, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      attributeOldValue: true,
    });
  }
})();

/* ------------------------------------------------------------------
 * 3. Carrousel d'avis Judge.me.
 *
 * Le markup vient des metachamps Judge.me, mais son script maison n'est pas
 * charge sur ce theme. Deux choses restent donc a faire nous-memes :
 * afficher les vignettes produit (Judge.me les met en "data-src" pour son
 * chargement differe) et faire defiler la rangee avec les fleches.
 * ------------------------------------------------------------------ */
(() => {
  const section = document.querySelector('.avis-clients');
  if (!section) return;

  // vignettes produit : on remet la vraie source
  for (const img of section.querySelectorAll('img[data-src]')) {
    img.src = img.dataset.src;
    if (img.dataset.srcRetina) img.srcset = `${img.dataset.src} 1x, ${img.dataset.srcRetina} 2x`;
    img.loading = 'lazy';
  }

  // fleches de defilement
  const rangee = section.querySelector('.jdgm-carousel__item-wrapper');
  if (!rangee) return;

  const pas = () => {
    const carte = rangee.querySelector('.jdgm-carousel-item');
    return carte ? carte.getBoundingClientRect().width + 20 : rangee.clientWidth * 0.8;
  };

  section.querySelector('.jdgm-carousel__left-arrow')
    ?.addEventListener('click', () => rangee.scrollBy({ left: -pas(), behavior: 'smooth' }));
  section.querySelector('.jdgm-carousel__right-arrow')
    ?.addEventListener('click', () => rangee.scrollBy({ left: pas(), behavior: 'smooth' }));
})();

/* ------------------------------------------------------------------
 * 4. Pop-up newsletter.
 *
 * Apparait apres un delai, une seule fois par visiteur pendant la duree
 * choisie dans les reglages. Si le visiteur vient de s'inscrire, Shopify
 * recharge la page : on rouvre alors le pop-up sur son message de bienvenue.
 * ------------------------------------------------------------------ */
(() => {
  const popup = document.getElementById('popup-newsletter');
  if (!popup || typeof popup.showModal !== 'function') return;

  const CLE = 'popup-newsletter-vu';
  const CLE_INSCRIT = 'popup-newsletter-inscrit';
  const jours = Number(popup.dataset.jours || 30);
  const delai = Number(popup.dataset.delai || 6) * 1000;

  // frequence a 0 = on le remontre a chaque nouvelle visite. On memorise alors
  // dans sessionStorage : il ne reapparait pas de page en page pendant la
  // visite en cours, mais revient au retour suivant sur le site.
  const parVisite = jours === 0;
  const stockage = () => (parVisite ? sessionStorage : localStorage);

  const memoriser = () => {
    try {
      stockage().setItem(CLE, parVisite ? 'vu' : String(Date.now() + jours * 86400000));
    } catch {
      /* navigation privee : on ne memorise pas, tant pis */
    }
  };

  const dejaVu = () => {
    try {
      const valeur = stockage().getItem(CLE);
      if (!valeur) return false;
      return parVisite ? true : Date.now() < Number(valeur);
    } catch {
      return false;
    }
  };

  const fermer = () => {
    memoriser();
    popup.close();
  };

  for (const bouton of popup.querySelectorAll('[data-fermer-popup]')) {
    bouton.addEventListener('click', fermer);
  }

  // clic en dehors de la carte
  popup.addEventListener('click', (e) => {
    if (e.target === popup) fermer();
  });

  // touche Echap
  popup.addEventListener('cancel', (e) => {
    e.preventDefault();
    fermer();
  });

  // deja inscrit une fois : on ne le represente plus jamais, quelle que soit
  // la frequence choisie dans les reglages.
  const dejaInscrit = () => {
    try {
      return localStorage.getItem(CLE_INSCRIT) === 'oui';
    } catch {
      return false;
    }
  };

  // le visiteur vient de s'inscrire : on montre le code sans attendre,
  // et on retient l'inscription de facon durable.
  const inscrit = popup.querySelector('.popup-news__code');
  if (inscrit) {
    try {
      localStorage.setItem(CLE_INSCRIT, 'oui');
    } catch {
      /* navigation privee */
    }
    popup.showModal();
    memoriser();
    return;
  }

  if (dejaInscrit()) return;
  if (dejaVu()) return;
  setTimeout(() => {
    if (!popup.open) popup.showModal();
  }, delai);
})();

/* ------------------------------------------------------------------
 * 5. Favoris.
 *
 * Chaque favori retient le produit ET la taille choisie, pour qu'on puisse
 * l'ajouter au panier directement depuis le tiroir. La liste est conservee
 * dans le navigateur du visiteur (localStorage) : aucune inscription, mais
 * elle ne suit pas d'un appareil a l'autre.
 * ------------------------------------------------------------------ */
(() => {
  const CLE = 'favoris-dressing-axelle';

  const lire = () => {
    try {
      const brut = JSON.parse(localStorage.getItem(CLE) || '[]');
      if (!Array.isArray(brut)) return [];
      // compatibilite : les premieres versions ne stockaient que le handle
      return brut.map((f) => (typeof f === 'string' ? { handle: f } : f)).filter((f) => f && f.handle);
    } catch {
      return [];
    }
  };

  const ecrire = (liste) => {
    try {
      localStorage.setItem(CLE, JSON.stringify(liste));
    } catch {
      /* navigation privee ou stockage plein */
    }
  };

  const prix = (centimes) =>
    (centimes / 100).toLocaleString('fr-BE', { minimumFractionDigits: 2 }) + ' €';

  const compteur = document.querySelector('[data-compteur-favoris]');

  const majAffichage = () => {
    const liste = lire();
    if (compteur) {
      compteur.textContent = String(liste.length);
      compteur.hidden = liste.length === 0;
    }
    const handles = liste.map((f) => f.handle);
    for (const bouton of document.querySelectorAll('[data-favori]')) {
      const actif = handles.includes(bouton.dataset.favori);
      bouton.classList.toggle('est-favori', actif);
      bouton.setAttribute('aria-pressed', String(actif));
    }
  };

  /* Le titre d'une variante contient toutes les options, y compris celles qui
     n'offrent aucun choix ("Blanc / S/M" quand il n'existe qu'un seul coloris).
     On ne garde que les options reellement multiples, comme sur la fiche produit. */
  const libelleVariante = (produit, variante) => {
    const multiples = (produit.options || []).map((_, i) => {
      const valeurs = new Set(produit.variants.map((v) => v.options[i]));
      return valeurs.size > 1;
    });
    const retenues = variante.options.filter((_, i) => multiples[i]);
    return retenues.length ? retenues.join(' / ') : variante.title;
  };

  const fiche = (handle) =>
    fetch(`/products/${handle}.js`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

  /* ---- choix de la taille ---------------------------------------- */
  let choix = null;

  const construireChoix = () => {
    choix = document.createElement('dialog');
    choix.className = 'favoris-choix';
    choix.innerHTML = `
      <p class="favoris-choix__titre"></p>
      <p class="favoris-choix__question">Quelle taille veux-tu enregistrer ?</p>
      <div class="favoris-choix__tailles"></div>
      <button type="button" class="favoris-choix__annuler">Annuler</button>`;
    document.body.appendChild(choix);
    choix.querySelector('.favoris-choix__annuler').addEventListener('click', () => choix.close());
    choix.addEventListener('click', (e) => {
      if (e.target === choix) choix.close();
    });
  };

  const demanderTaille = (produit) =>
    new Promise((resoudre) => {
      if (!choix) construireChoix();
      choix.querySelector('.favoris-choix__titre').textContent = produit.title;
      const zone = choix.querySelector('.favoris-choix__tailles');
      zone.innerHTML = '';

      for (const v of produit.variants) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'favoris-choix__taille';
        b.textContent = libelleVariante(produit, v);
        if (!v.available) {
          b.classList.add('favoris-choix__taille--epuise');
          b.title = 'Épuisé';
        }
        b.addEventListener('click', () => {
          choix.close();
          resoudre(v);
        });
        zone.appendChild(b);
      }

      choix.addEventListener('close', () => resoudre(null), { once: true });
      choix.showModal();
    });

  /* ---- clic sur un coeur ----------------------------------------- */
  document.addEventListener('click', async (e) => {
    const bouton = e.target.closest('[data-favori]');
    if (!bouton) return;
    e.preventDefault();
    e.stopPropagation();

    const handle = bouton.dataset.favori;
    const liste = lire();
    const i = liste.findIndex((f) => f.handle === handle);

    if (i !== -1) {
      liste.splice(i, 1);
      ecrire(liste);
      majAffichage();
      if (tiroir?.open) remplir();
      return;
    }

    const produit = await fiche(handle);
    if (!produit) return;

    let variante = produit.variants[0];
    if (produit.variants.length > 1) {
      variante = await demanderTaille(produit);
      if (!variante) return; // annule
    }

    liste.push({ handle, id: variante.id, taille: variante.title });
    ecrire(liste);
    majAffichage();
    bouton.classList.add('favori--anime');
    setTimeout(() => bouton.classList.remove('favori--anime'), 400);
    if (tiroir?.open) remplir();
  });

  /* ---- panier ----------------------------------------------------- */
  const majPastillePanier = async () => {
    try {
      const c = await fetch('/cart.js').then((r) => r.json());
      for (const bulle of document.querySelectorAll('.cart-bubble')) {
        bulle.classList.toggle('visually-hidden', c.item_count === 0);
        const n = bulle.querySelector('.cart-bubble__text-count');
        if (n) {
          n.textContent = String(c.item_count);
          n.classList.toggle('hidden', c.item_count === 0);
        }
      }
    } catch {
      /* sans importance : la pastille se remettra a jour au chargement suivant */
    }
  };

  /* Ajouter au panier ne suffit pas : le tiroir du panier est rendu au
     chargement de la page, il resterait donc figé sur l'ancien contenu. On
     demande a Shopify de renvoyer le HTML a jour de la section, on le remplace,
     puis on previent le theme pour qu'il rafraichisse la pastille. */
  const ajouterAuPanier = async (id) => {
    const zone = document.querySelector('cart-items-component[data-section-id]');
    const sectionId = zone?.dataset.sectionId;

    const corpsRequete = new FormData();
    corpsRequete.append('id', String(id));
    corpsRequete.append('quantity', '1');
    if (sectionId) corpsRequete.append('sections', sectionId);

    let reponse;
    try {
      reponse = await fetch('/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: corpsRequete,
      }).then((r) => r.json());
    } catch {
      return false;
    }

    // Shopify renvoie un champ "status" uniquement en cas d'erreur
    if (reponse?.status) return false;

    /* On remplace tout l'interieur du tiroir, pas seulement la liste des
       articles : la barre de livraison gratuite est un bloc a part, elle
       resterait sinon figee sur l'ancien montant.
       On synchronise aussi la classe "panier vide" portee par la fenetre :
       posee au chargement, elle deforme la mise en page une fois le panier
       rempli (en-tete coupe, contenu centre verticalement). */
    const html = reponse?.sections?.[sectionId];
    if (html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const interieurNeuf = doc.querySelector('.cart-drawer__inner');
      const interieur = document.querySelector('.cart-drawer__inner');
      if (interieurNeuf && interieur) interieur.replaceWith(interieurNeuf);

      const fenetreNeuve = doc.querySelector('.cart-drawer__dialog');
      const fenetre = document.querySelector('.cart-drawer__dialog');
      if (fenetreNeuve && fenetre) {
        fenetre.classList.toggle(
          'cart-drawer--empty',
          fenetreNeuve.classList.contains('cart-drawer--empty')
        );
      }
    }

    /* On ne diffuse volontairement PAS l'evenement "cart:update" du theme :
       sans le detail complet des sections, il declenche un rechargement de tout
       l'en-tete — or le tiroir des favoris s'y trouve, et son contenu serait
       efface sous les yeux du visiteur. On met donc la pastille a jour nous-memes. */
    await majPastillePanier();

    return true;
  };

  /* ---- tiroir ----------------------------------------------------- */
  const tiroir = document.getElementById('favoris-tiroir');
  if (!tiroir) {
    majAffichage();
    return;
  }

  const corps = tiroir.querySelector('[data-liste-favoris]');
  const vide = tiroir.querySelector('[data-favoris-vide]');
  const pied = tiroir.querySelector('[data-pied-favoris]');

  async function remplir() {
    const favoris = lire();
    corps.querySelectorAll('.favoris-article').forEach((n) => n.remove());

    if (favoris.length === 0) {
      vide.hidden = false;
      pied.hidden = true;
      return;
    }
    vide.hidden = true;
    pied.hidden = false;

    const fiches = await Promise.all(favoris.map((f) => fiche(f.handle)));

    fiches.forEach((p, i) => {
      const fav = favoris[i];
      const article = document.createElement('div');
      article.className = 'favoris-article';

      if (!p) {
        article.innerHTML = `<div class="favoris-article__infos">
            <span class="favoris-article__titre">Produit indisponible</span>
            <span class="favoris-article__prix">Il n'est plus en ligne</span>
          </div>
          <button type="button" class="favoris-article__retirer" data-favori="${fav.handle}" aria-label="Retirer">&times;</button>`;
        corps.appendChild(article);
        return;
      }

      // la variante enregistree existe-t-elle toujours ?
      const v = p.variants.find((x) => x.id === fav.id) || p.variants[0];
      const image = p.featured_image ? `${p.featured_image}&width=160` : '';

      article.innerHTML = `
        <a class="favoris-article__media" href="${p.url}">
          ${image ? `<img src="${image}" alt="" loading="lazy">` : ''}
        </a>
        <div class="favoris-article__infos">
          <a class="favoris-article__titre" href="${p.url}">${p.title}</a>
          ${p.variants.length > 1 ? `<span class="favoris-article__taille">Taille ${libelleVariante(p, v)}</span>` : ''}
          <span class="favoris-article__prix">${prix(v.price)}</span>
          <button type="button" class="favoris-article__ajouter" data-ajouter="${v.id}" ${v.available ? '' : 'disabled'}>
            ${v.available ? 'Ajouter au panier' : 'Épuisé'}
          </button>
        </div>
        <button type="button" class="favoris-article__retirer" data-favori="${p.handle}" aria-label="Retirer des favoris">&times;</button>`;
      corps.appendChild(article);
    });
  }

  corps.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-ajouter]');
    if (!b) return;
    b.disabled = true;
    const libelle = b.textContent;
    b.textContent = 'Ajout…';
    const ok = await ajouterAuPanier(Number(b.dataset.ajouter));
    b.textContent = ok ? 'Ajouté ✓' : 'Réessayer';
    setTimeout(() => {
      b.textContent = libelle;
      b.disabled = false;
    }, 1800);
  });

  document.querySelector('[data-ouvrir-favoris]')?.addEventListener('click', () => {
    remplir();
    tiroir.showModal();
  });

  for (const b of tiroir.querySelectorAll('[data-fermer-favoris]')) {
    b.addEventListener('click', () => tiroir.close());
  }
  tiroir.addEventListener('click', (e) => {
    if (e.target === tiroir) tiroir.close();
  });

  majAffichage();
})();

/* ------------------------------------------------------------------
 * Compte a rebours « panier reserve ».
 *
 * Le tiroir du panier est re-rendu a chaque ajout / retrait / changement de
 * quantite : un chrono garde en memoire dans l'element serait remis a zero a
 * chaque fois. On garde donc l'heure de depart dans sessionStorage et un seul
 * intervalle reecrit le texte, quel que soit l'element present a l'ecran.
 *
 * Le chrono repart de cinq minutes des que le contenu du panier change
 * (l'attribut data-panier-articles porte le nombre d'articles rendu par
 * Liquid) et se remet a zero a la fermeture de l'onglet (sessionStorage).
 * ------------------------------------------------------------------ */
(() => {
  const DUREE = 5 * 60 * 1000;
  const CLE_DEBUT = 'lda-panier-debut';
  const CLE_ARTICLES = 'lda-panier-articles';

  /* la navigation privee de certains navigateurs interdit sessionStorage */
  const lire = (cle) => {
    try {
      return sessionStorage.getItem(cle);
    } catch {
      return null;
    }
  };
  const ecrire = (cle, valeur) => {
    try {
      sessionStorage.setItem(cle, valeur);
    } catch {
      /* tant pis : le chrono repartira de cinq minutes a chaque affichage */
    }
  };

  const formater = (ms) => {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(total / 60);
    const secondes = total % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secondes).padStart(2, '0')}`;
  };

  /* memoire de secours quand sessionStorage est indisponible */
  let memoireDebut = 0;
  let memoireArticles = null;

  const tic = () => {
    const bloc = document.querySelector('[data-panier-timer]');
    if (!bloc) return;

    const articles = bloc.dataset.panierArticles ?? '0';
    let debut = Number(lire(CLE_DEBUT)) || memoireDebut;

    /* panier modifie (ou premiere ouverture) : on repart de cinq minutes */
    const articlesConnus = lire(CLE_ARTICLES) ?? memoireArticles;
    if (!debut || articlesConnus !== articles) {
      debut = Date.now();
      memoireDebut = debut;
      memoireArticles = articles;
      ecrire(CLE_DEBUT, String(debut));
      ecrire(CLE_ARTICLES, articles);
    }

    const restant = debut + DUREE - Date.now();
    bloc.classList.toggle('panier-timer--fini', restant <= 0);

    const valeur = bloc.querySelector('[data-panier-timer-valeur]');
    if (valeur) valeur.textContent = formater(restant);
  };

  tic();
  setInterval(tic, 1000);
})();
